import { useContext, useEffect, useState } from "react";
import { Alert, Button } from "antd";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { API_REQUEST_HEADERS_BASEURL } from "../../utils/Constants";

export default function FleetPlanBanner() {
  const { fleetId, fleetRole } = useContext(AuthContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [access, setAccess] = useState(null);

  useEffect(() => {
    setAccess(null);
    if (!fleetId) return undefined;
    let disposed = false;
    let inFlight = false;
    const refresh = async () => {
      if (inFlight || document.visibilityState === "hidden") return;
      inFlight = true;
      try {
        const { data } = await API_REQUEST_HEADERS_BASEURL.get(`/fleets/${fleetId}/operations-access`);
        if (!disposed) setAccess(data);
      } catch (error) {
        // A network error must not be presented as an expired subscription.
        console.warn("Could not refresh fleet plan access", error?.response?.status || error?.code);
      } finally {
        inFlight = false;
      }
    };
    const onBlocked = (event) => {
      if (event.detail?.fleetId === fleetId) setAccess(event.detail.access);
    };
    void refresh();
    const interval = setInterval(refresh, 60000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("fleet-plan-blocked", onBlocked);
    return () => {
      disposed = true;
      clearInterval(interval);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("fleet-plan-blocked", onBlocked);
    };
  }, [fleetId, pathname]);

  useEffect(() => {
    if (!access?.active || !access.expiresAt) return undefined;
    const delay = new Date(access.expiresAt).getTime() - Date.now();
    // Long intervals are checked by the regular refresh; never overflow setTimeout.
    if (delay > 2147483647) return undefined;
    const timer = setTimeout(() => setAccess((current) => ({ ...current, active: false, reason: "expired" })), Math.max(0, delay));
    return () => clearTimeout(timer);
  }, [access]);

  const isTrial = access?.active && access.source === "trial";
  if (!access || (access.active && !isTrial)) return null;
  const remainingDays = access.expiresAt
    ? Math.max(1, Math.ceil((new Date(access.expiresAt).getTime() - Date.now()) / 86400000))
    : null;
  const canManage = ["OWNER", "EXECUTIVE"].includes(fleetRole);
  return (
    <div style={{ position: "sticky", top: 0, zIndex: 900 }}>
      <Alert
        banner showIcon type={isTrial ? "info" : "warning"}
        message={isTrial
          ? <>{t(remainingDays === null ? "billing.operations.trial" : "billing.operations.trialDays", { count: remainingDays })}{access.trialUsage && <> · {t('trialEDocUsage', access.trialUsage)}</>}</>
          : t(access.reason === "expired" ? "billing.operations.expired" : "billing.operations.required")}
        action={canManage
          ? <Button size="small" onClick={() => navigate("/billing")}>{t("billing.operations.manage")}</Button>
          : <span>{t("billing.operations.contactAdmin")}</span>}
      />
    </div>
  );
}
